import {
  Button,
  Checkbox,
  Input,
  MenuItem,
  PhoneInput,
  Select,
  Text,
  VSpacer,
} from '@/components/DesignSystem';
import { useAddRetailerMember, useUpdateMember } from '@/hooks/useRetailer';
import { SetErrorFunction } from '@/pages/Admin/HierarchyOfRetailers/Locations/Types';
import { RetailerLocationEndpoint } from '@api/endpoints';
import { Alert, Dialog, DialogActions, DialogContent, Stack, useTheme } from '@mui/material';
import { UserPermission, UserPermissions, UserType } from '@shared/enums';
import { getObjectChanges } from '@shared/utilities';
import React, { Dispatch, SetStateAction, useMemo, useState } from 'react';
import { useParams } from 'react-router-dom';
import { Location } from '../Locations/Types';
import { useQuery } from 'react-query';
import { UserApi } from '@/utilities/api/UserApi';
import { QueryKeys } from '@/constants/QueryKeys';

type MemberType = {
  businessName?: string,
  email?: string,
  id?: string,
  isTest: boolean,
  permissions: UserPermissions,
  role?: string,
  telephone?: string,
};

type AddMemberProps = {
  isAddLocation?: boolean,
  isLocation?: boolean,
  isLoading?: boolean,
  isEdit?: boolean,
  member?: MemberType,
  onClose: () => void,
  onSaveMember?: () => void,
  serverError?: string,
  setAddOrEditLocation?: Dispatch<SetStateAction<Location>>,
  setErrorMessage?: SetErrorFunction,
  show: boolean;
};

const MemberFormModal = ({
  isAddLocation = false,
  isLocation = false,
  isLoading = false,
  isEdit,
  member = {
    businessName: '',
    email: '',
    id: '',
    isTest: false,
    permissions: {},
    role: 'retailer-admin',
    telephone: '',
  },
  onClose,
  onSaveMember,
  serverError = '',
  setAddOrEditLocation,
  setErrorMessage,
  show,
}: AddMemberProps) => {
  const [memberDetails, setMemberDetails] = useState(member);
  const [error, setError] = useState('');
  const theme = useTheme();

  const { id = '', locationId = '' } = useParams<{
    id: string;
    locationId: string;
  }>();

  const usersWithPermissionBody = {
    permissions: [UserPermission.CanAccessNotificationsBuilder],
    retailerId: id,
    userType: [UserType.RetailerAdmin],
  };
  const { data: usersWithPermissionCount } = useQuery(
    [QueryKeys.LIST_USERS, usersWithPermissionBody],
    async () => {
      const users = await UserApi.list(usersWithPermissionBody);
      return users.data.filter((user) => (
        user.permissions?.canAccessNotificationsBuilder
        && user.id !== member.id
      )).length;
    },
  );

  const { addRetailerMember, isLoading: isAddMemberLoading } =
  useAddRetailerMember(onClose, setError);

  const { updateMember, isLoading: isUpdateMemberLoading } =
    useUpdateMember(member?.id ?? '', isLocation, onClose, setError);

  const onChange = (text: string = 'string', type: string) => {
    if (type === 'telephone' || type === 'email') {
      setError('');
      setErrorMessage && setErrorMessage((prev) => ({
        ...prev,
        serverError: '',
      }));
    }
    setMemberDetails({ ...memberDetails, [type]: text?.trimStart() });
    if (isAddLocation && setAddOrEditLocation) {
      if (text === undefined) {
        text = '';
      }
      setAddOrEditLocation((prevState: RetailerLocationEndpoint.Create.Request) => {
        const salesperson =
          prevState.salesperson ||
            { businessName: '', email: '', telephone: '', userType: UserType.SalesPerson };
        return {
          ...prevState,
          salesperson: {
            ...salesperson,
            [type]: text?.trimStart() || '',
          },
        };
      });
    }
  };

  const isNotificationBuilderAccessDisabled = (
    !!usersWithPermissionCount
    && usersWithPermissionCount >= 2
    && !memberDetails?.permissions?.canAccessNotificationsBuilder
  );

  const isValid = useMemo(() => {
    if (!memberDetails.businessName?.trim()) {
      return false;
    }
    const email = memberDetails.email?.trim();
    if (!email || !email.includes('@')) {
      return false;
    }
    if (!memberDetails.telephone?.trim()) {
      return false;
    }
    if (isEdit) {
      const hasChanges = (
        memberDetails.businessName !== member.businessName
        || memberDetails.email !== member.email
        || memberDetails.telephone !== member.telephone
        || memberDetails.isTest !== member.isTest
        || JSON.stringify(memberDetails.permissions) !== JSON.stringify(member.permissions)
      );
      if (!hasChanges) {
        return false;
      }
    }
    return true;
  }, [memberDetails, isEdit, member]);

  const onSave = () => {
    if (isAddLocation && onSaveMember) {
      return onSaveMember();
    }
    const userType = (
      isLocation ? UserType.SalesPerson : UserType.RetailerAdmin
    ) as UserType.RetailerAdmin | UserType.SalesPerson;

    delete memberDetails.role;

    const updatedMember = {
      ...memberDetails,
      businessName: memberDetails?.businessName?.trim() || '',
      email: memberDetails?.email?.trim() || '',
      isTest: memberDetails?.isTest ?? false,
      locationId: locationId || '',
      permissions: memberDetails?.permissions || {},
      retailerId: id,
      telephone: memberDetails?.telephone || '',
      userType: userType,
    };

    if (isEdit) {
      const changedMemberData = getObjectChanges(member, memberDetails);
      if (JSON.stringify(member.permissions) !== JSON.stringify(memberDetails.permissions)) {
        changedMemberData.permissions = memberDetails.permissions;
      }
      
      updateMember(changedMemberData);
    } else {
      addRetailerMember(updatedMember);
    }
  };

  return (
    <Dialog
      data-testid='add-edit-member'
      onClose={onClose}
      open={show}
      scroll='body'
    >
      <DialogContent sx={{
        width: '560px',
      }}>
      <Stack>
        {!!error && (
        <Alert color='error' icon={false}>{error}</Alert>
        )}
        {
          !!serverError && (
            <Alert color='error' icon={false}>{serverError}</Alert>
          )
        }
        <VSpacer size='4' />
        <Stack spacing={4}>
          <Text category='h4'>{isEdit ? 'Edit' : 'Add'} Member</Text>
          <Input
            label='Member Name'
            onChangeText={(e) => onChange(e, 'businessName')}
            required
            testID='member-name'
            value={memberDetails.businessName}
          />
          <PhoneInput
            label='Phone Number'
            onChangeText={(e) => onChange(e, 'telephone')}
            required
            testID='salesPerson-telephone'
            value={memberDetails.telephone}
          />
          <Input
            label='Email'
            onChangeText={(e) => onChange(e, 'email')}
            required
            testID='member-email'
            value={memberDetails.email}
          />
          <Select
            disabled
            label='Role'
            required
            testID='select-role'
            value={
              isLocation || isAddLocation ? 'sales-person' : memberDetails.role
            }
          >
            <MenuItem testID={UserType.RetailerAdmin} value={'retailer-admin'}>
              Retailer Admin
            </MenuItem>
            <MenuItem testID={UserType.SalesPerson} value={'sales-person'}>
              Salesperson
            </MenuItem>
          </Select>
          <Checkbox
            checked={memberDetails?.isTest ?? false}
            onChangeChecked={
              (checked) => setMemberDetails({ ...memberDetails, isTest: checked })
            }
            testID="is-test-user-checkbox">
            <Text category="body-large">Test User</Text>
          </Checkbox>
          {!isLocation && !isAddLocation && (
            <>
              <Checkbox
                checked={memberDetails?.permissions?.canAccessNotificationsBuilder ?? false}
                disabled={
                  usersWithPermissionCount === undefined || usersWithPermissionCount >= 2
                }
                onChangeChecked={
                  (checked) => setMemberDetails({
                    ...memberDetails,
                    permissions: {
                      ...memberDetails.permissions,
                      [UserPermission.CanAccessNotificationsBuilder]: checked,
                    },
                  })
                }
                testID="notification-builder-access-checkbox">
                <Text
                  category="body-large"
                  {...isNotificationBuilderAccessDisabled
                    ? { sx: { color: theme.palette.grey[700] } }
                    : {}
                  }
                >
                  Grant access to Notification Builder
                </Text>
              </Checkbox>
              {isNotificationBuilderAccessDisabled && (
                <Text sx={{ color: theme.palette.grey[700] }}>
                  The maximum number of admin users assigned to access the
                  Notification Builder has been reached.
                </Text>
              )}
              <Checkbox
                checked={memberDetails?.permissions?.canAccessReports ?? false}
                onChangeChecked={
                  (checked) => setMemberDetails({
                    ...memberDetails,
                    permissions: {
                      ...memberDetails.permissions,
                      [UserPermission.CanAccessReports]: checked,
                    },
                  })
                }
                testID="reports-access-checkbox">
                <Text category="body-large">
                  Grant access to Reports
                </Text>
              </Checkbox>
            </>
          )}
        </Stack>
      </Stack>
      </DialogContent>
      <DialogActions sx={{ pb: '2rem', pt: '2rem' }}>
        <Button disabled={isLoading || isAddMemberLoading || isUpdateMemberLoading}
        onClick={onClose} testID='cancel' variant='text'>
          Cancel
        </Button>
        <Button
          disabled={!isValid || !!error || !!serverError}
          loading={isLoading || isAddMemberLoading || isUpdateMemberLoading}
          onClick={onSave}
          testID='save'
        >
          Save
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default MemberFormModal;
